// Tailwind Plus Elements: <el-dialog>, <el-dropdown>, <el-menu>, … and the command/commandfor polyfill.
import "@tailwindplus/elements";
import {
  loadMatcherTemplate,
  initMatcherController
} from "./js/matcherController.js";
import { showToast, dismissToast } from "./js/ui/toast.js";
import { installCopyButtons } from "./js/ui/copy.js";
import { renderNav, setActiveNav, closeMobileSidebar, initNavShortcuts } from "./js/ui/shell.js";
import { initCommandPalette } from "./js/ui/palette.js";
import { hideDialog } from "./js/ui/dialog.js";

// 0. Theme Manager (System / Light / Dark Mode), chosen in the user menu
function initTheme() {
  const savedTheme = localStorage.getItem("crm_theme") || "system";
  applyTheme(savedTheme);
  markThemeChoice(savedTheme);

  document.querySelectorAll("[data-theme-choice]").forEach((item) => {
    item.addEventListener("click", () => {
      const mode = item.dataset.themeChoice;
      localStorage.setItem("crm_theme", mode);
      applyTheme(mode);
      markThemeChoice(mode);
    });
  });

  if (window.matchMedia) {
    window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
      const current = localStorage.getItem("crm_theme") || "system";
      if (current === "system") {
        applyTheme("system");
      }
    });
  }
}

function applyTheme(mode) {
  let isDark = false;
  if (mode === "system") {
    isDark = Boolean(window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches);
  } else {
    isDark = mode === "dark";
  }

  if (isDark) {
    document.documentElement.setAttribute("data-theme", "dark");
  } else {
    document.documentElement.removeAttribute("data-theme");
  }
}

function markThemeChoice(mode) {
  document.querySelectorAll("[data-theme-choice]").forEach((item) => {
    if (item.dataset.themeChoice === mode) item.setAttribute("aria-current", "true");
    else item.removeAttribute("aria-current");
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initTheme);
} else {
  initTheme();
}

import { 
  loadCategoriesTemplate,
  renderCategoriesView as renderCategoriesViewFromModule, 
  toggleCategoriesRawJson as toggleCategoriesRawJsonFromModule, 
  saveCategoriesDocument as saveCategoriesDocumentFromModule,
  getActiveCategoriesData as getActiveCategoriesDataFromModule,
  toggleCategoryFeatured as toggleCategoryFeaturedFromModule,
  deleteCategoryItem as deleteCategoryItemFromModule,
  openCategoryModal as openCategoryModalFromModule,
  saveCategoryModalSubmit as saveCategoryModalSubmitFromModule
} from "./js/categoriesController.js";
import {
  loadUsersTemplate,
  initUsersController,
  resetUserSearchState,
  performUserSearch,
  toggleUserDeveloperMode,
  toggleUserTesterMode,
  toggleUserAdminRole,
  toggleSubRawJson,
  copySubRawJson
} from "./js/usersController.js";
import { loadImportCacheTemplate, initImportCacheController } from "./js/importCacheController.js";
import {
  loadFeedbackTemplate,
  initFeedbackController,
  userIdFromHash,
  fetchFeedbackList,
  fetchRecipeRatings,
  renderFeedbackTable
} from "./js/feedbackController.js";
import { loadInboxTemplate, initInboxController, enterInbox, leaveInbox, startInboxAutoSync } from "./js/inboxController.js";
import { loadHistoryTemplate, initHistoryController, openHistoryFromHash } from "./js/historyController.js";
import { loadFamiliesTemplate, initFamiliesController, openFamilyFromHash } from "./js/familiesController.js";
import {
  loadPricingTemplate,
  initPricingController,
  fetchAndRenderPricing
} from "./js/pricingController.js";
import { loadStoreSearchTemplate, initStoreSearchController } from "./js/storeSearchController.js";
import { loadKrogerCheckTemplate, initKrogerCheckController } from "./js/krogerCheckController.js";
import { loadBillingTemplate, initBillingController, openBilling } from "./js/billingController.js";
import { loadAlertsTemplate, initAlertsController, openAlerts, refreshAlerts, startAlertsAutoRefresh } from "./js/alertsController.js";
import {
  loadLeadsTemplate,
  initLeadsController,
  fetchLeadsList,
  fetchPromoInventoryStock,
  openLeadModal,
  resetLeadClaim,
  copyFilteredEmails,
  exportLeadsCSV
} from "./js/leadsController.js";
import {
  loadAnnouncementsTemplate,
  initAnnouncementsController,
  loadAnnouncements,
  fetchAnnouncementHistory
} from "./js/announcementsController.js";
import { 
  loadDuplicatesTemplate,
  normalizeIngredientKey as normalizeIngredientKeyFromModule,
  findDuplicateClusters as findDuplicateClustersFromModule,
  refreshDuplicatesEngine as refreshDuplicatesEngineFromModule,
  renderDuplicatesSection as renderDuplicatesSectionFromModule,
  autoMergeAllCleanClusters as autoMergeAllCleanClustersFromModule,
  openMergeDiffModal as openMergeDiffModalFromModule,
  setSurvivingUid as setSurvivingUidFromModule,
  selectMergeField as selectMergeFieldFromModule,
  executeIngredientMerge as executeIngredientMergeFromModule
} from "./js/duplicatesResolver.js";
import { 
  loadIngredientsTemplate,
  renderIngredientsTable as renderIngredientsTableFromModule,
  openIngredientInspector as openIngredientInspectorFromModule,
  openIngredientEditor as openIngredientEditorFromModule,
  openAddMissingIngredientForm as openAddMissingIngredientFormFromModule,
  validateIngredientDuplicates as validateIngredientDuplicatesFromModule,
  updateActiveCategoriesData as updateActiveCategoriesDataFromModule,
  updateActiveMasterIngredients as updateActiveMasterIngredientsFromModule,
  saveMasterIngredient as saveMasterIngredientFromModule,
  deleteMasterIngredient as deleteMasterIngredientFromModule,
  copyRawIngredient as copyRawIngredientFromModule,
  onIngredientSearchInput as onIngredientSearchInputFromModule,
  setIngredientSort as setIngredientSortFromModule,
  toggleIngredientAllergenTag as toggleIngredientAllergenTagFromModule,
  toggleIngredientDietTag as toggleIngredientDietTagFromModule,
  onSubstituteSearchInput as onSubstituteSearchInputFromModule,
  onSubstituteChosen as onSubstituteChosenFromModule,
  addSubstitute as addSubstituteFromModule,
  removeSubstitute as removeSubstituteFromModule,
  updateSubstituteReason as updateSubstituteReasonFromModule,
  autofillIngredientWithAI as autofillIngredientWithAIFromModule,
  downloadMasterIngredientsJson as downloadMasterIngredientsJsonFromModule,
  openIngredientImportJsonModal as openIngredientImportJsonModalFromModule,
  onIngredientJsonInput as onIngredientJsonInputFromModule,
  onIngredientJsonFileSelected as onIngredientJsonFileSelectedFromModule,
  applyIngredientJsonImport as applyIngredientJsonImportFromModule,
  generateAiPhotoForIngredient as generateAiPhotoForIngredientFromModule,
  onIngredientImagePathChanged as onIngredientImagePathChangedFromModule,
  updateActiveMasterIngredientsPrefix as updateActiveMasterIngredientsPrefixFromModule,
  validateIngredient
} from "./js/ingredientsController.js";
import { rebuildPlanningIndex as rebuildPlanningIndexFromModule } from "./js/planningIndex.js";
import { checkRecipeEditable } from "./js/recipeEditGuard.js";
import {
  loadRecipesTemplate,
  renderRecipeTable as renderRecipeTableFromModule,
  onRecipeSearchInput as onRecipeSearchInputFromModule,
  updateActiveCatalogRecipes as updateActiveCatalogRecipesFromModule,
  openRecipeEditor as openRecipeEditorFromModule,
  addIngredientRow as addIngredientRowFromModule,
  addInstructionRow as addInstructionRowFromModule,
  saveRecipe as saveRecipeFromModule,
  deleteRecipe as deleteRecipeFromModule,
  toggleRecipeSelection as toggleRecipeSelectionFromModule,
  toggleSelectAllRecipes as toggleSelectAllRecipesFromModule,
  clearRecipeSelection as clearRecipeSelectionFromModule,
  getSelectedRecipeIds as getSelectedRecipeIdsFromModule,
  bulkDeleteRecipes as bulkDeleteRecipesFromModule,
  bulkUpdateRecipeStatus as bulkUpdateRecipeStatusFromModule,
  bulkUpdateRecipeSignature as bulkUpdateRecipeSignatureFromModule,
  printRecipeJson as printRecipeJsonFromModule,
  loadRecipeViewTemplate as loadRecipeViewTemplateFromModule,
  renderRecipeViewSection as renderRecipeViewSectionFromModule,
  viewRecipe as viewRecipeFromModule,
  toggleRecipeViewRawJson as toggleRecipeViewRawJsonFromModule,
  setRecipeViewTimeline as setRecipeViewTimelineFromModule,
  setRecipeViewVariant as setRecipeViewVariantFromModule,
  quickFixIngredientUnit as quickFixIngredientUnitFromModule,
  updateActiveCategoriesData as updateActiveCategoriesDataInRecipesModule,
  updateActiveMasterIngredientsData as updateActiveMasterIngredientsInRecipesModule,
  toggleInlineTaxonomyEdit as toggleInlineTaxonomyEditFromModule,
  getCurrentRecipeViewData as getCurrentRecipeViewDataFromModule,
  saveTaxonomy as saveTaxonomyFromModule,
  updateTaxPillStyle as updateTaxPillStyleFromModule,
  toggleInlineCookwareEdit as toggleInlineCookwareEditFromModule,
  saveCookware as saveCookwareFromModule,
  toggleInlineMetricsEdit as toggleInlineMetricsEditFromModule,
  saveMetrics as saveMetricsFromModule,
  toggleUnifiedRecipeEdit as toggleUnifiedRecipeEditFromModule,
  addIngredientEditRow as addIngredientEditRowFromModule,
  addInstructionStepEditRow as addInstructionStepEditRowFromModule,
  reindexInstructionStepNumbers as reindexInstructionStepNumbersFromModule,
  saveUnifiedRecipe as saveUnifiedRecipeFromModule,
  openImportRecipeModal as openImportRecipeModalFromModule,
  switchImportTab as switchImportTabFromModule,
  submitImportRecipe as submitImportRecipeFromModule,
  renderRecipeNewSection as renderRecipeNewSectionFromModule,
  saveNewRecipe as saveNewRecipeFromModule,
  addNewRecipeIngredientRow as addNewRecipeIngredientRowFromModule,
  toggleNewRecipeRawJson as toggleNewRecipeRawJsonFromModule,
  copyNewRecipeRawJson as copyNewRecipeRawJsonFromModule,
  handleColumnSort as handleColumnSortFromModule,
  onRecipeSortDropdownChange as onRecipeSortDropdownChangeFromModule,
  addNewRecipeStepRow as addNewRecipeStepRowFromModule,
  removeNewRecipeStepRow as removeNewRecipeStepRowFromModule,
  setRecipeStatusFilter as setRecipeStatusFilterFromModule,
  generateAiPhotoForRecipe as generateAiPhotoForRecipeFromModule,
  regenerateRecipePhoto as regenerateRecipePhotoFromModule,
  toggleRecipePublishStatus as toggleRecipePublishStatusFromModule,
  onNewRecipeStatusChange as onNewRecipeStatusChangeFromModule,
  addRecipeNewCookwareChip as addRecipeNewCookwareChipFromModule,
  removeRecipeNewCookwareChip as removeRecipeNewCookwareChipFromModule,
  renderRecipeAuditLogDOM as renderRecipeAuditLogDOMFromModule,
  refreshRecipeAuditLog as refreshRecipeAuditLogFromModule,
  copyRecipeSourceUrl as copyRecipeSourceUrlFromModule,
  openAddRecipeJsonModal as openAddRecipeJsonModalFromModule,
  pasteExampleRecipeJson as pasteExampleRecipeJsonFromModule,
  submitAddRecipeJson as submitAddRecipeJsonFromModule,
  handleRecipeJsonDragOver as handleRecipeJsonDragOverFromModule,
  handleRecipeJsonDragLeave as handleRecipeJsonDragLeaveFromModule,
  handleRecipeJsonDrop as handleRecipeJsonDropFromModule,
  triggerRecipePhotoUpload as triggerRecipePhotoUploadFromModule,
  uploadRecipePhotoFromLocal as uploadRecipePhotoFromLocalFromModule
} from "./js/recipesController.js";
import { requestNotificationPermission } from "./js/utils/notifications.js";
import { syncPreviewCookie } from "./js/utils/previewCookie.js";
import { 
  loadDashboardTemplate,
  renderDashboardStats as renderDashboardStatsFromModule
} from "./js/dashboardController.js";
import { loadSubscriptionMetrics, renderSubscriptionsPanel } from "./js/subscriptionsPanel.js";
import { loadStoreRatings, renderStoreRatingsPanel } from "./js/storeRatingsPanel.js";
import { loadUsageLimits, renderUsagePanel } from "./js/usagePanel.js";
import { rememberDisclosures } from "./js/ui/disclosure.js";
import { listenToCatalog } from "./js/catalogListeners.js";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithCustomToken,
  signOut,
  onAuthStateChanged,
  onIdTokenChanged,
  connectAuthEmulator
} from "firebase/auth";
import {
  initializeAppCheck,
  ReCaptchaV3Provider,
  CustomProvider,
  getToken
} from "firebase/app-check";
import {
  getFirestore,
  connectFirestoreEmulator,
  doc,
  getDoc, 
  setDoc, 
  deleteDoc, 
  writeBatch,
  collection, 
  collectionGroup,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  getCountFromServer,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  onSnapshot
} from "firebase/firestore";
import { 
  getAI, 
  getGenerativeModel, 
  GoogleAIBackend,
  SchemaType,
  ResponseModality
} from "firebase/ai";
import { getFunctions, httpsCallable, connectFunctionsEmulator } from "firebase/functions";
import {
  isEmulatorMode,
  wantsLocalAdminSignIn,
  localAdminCustomToken,
  fakeAppCheckToken,
  EMULATOR_HOST,
  EMULATOR_PORTS
} from "./js/utils/emulators.js";
import { checkAdminAccess, accessDeniedMessage } from "./js/utils/adminAccess.js";
import { prefersRedirectSignIn, isSupersededSignIn } from "./js/utils/googleSignIn.js";

// Authoritative Firebase Web Configuration
const firebaseConfig = {
  apiKey: "AIzaSyBVG8gVudJX2oCFDGP1WrqYFgL43CJHG-w",
  // The CRM's own host (#1479). The sign-in result is stored by the handler on authDomain and read
  // back by an iframe from it, and WebKit (every iOS browser, Safari on the Mac) partitions that
  // iframe's storage by the top page's origin: with weeknightbiteapp.com a redirect came back with
  // no result and no error. Needs https://admin.weeknightbiteapp.com/__/auth/handler among the OAuth
  // web client's authorized redirect URIs.
  authDomain: "admin.weeknightbiteapp.com",
  projectId: "weeknightbite-83965",
  storageBucket: "weeknightbite-83965.firebasestorage.app",
  messagingSenderId: "1081882026390",
  appId: "1:1081882026390:web:8f85b84382886965303db7",
  measurementId: "G-DV3YR0NQ9E"
};

// Enable App Check debug mode for non-production/test environments if needed
self.FIREBASE_APPCHECK_EXECUTE_IN_TEST_BUILD = true;

// Initialize Single Firebase App Instance
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const functions = getFunctions(app);

// Local emulator mode (js/utils/emulators.js): localhost with ?emulators only.
const useEmulators = isEmulatorMode(window.location);
if (useEmulators) {
  connectAuthEmulator(auth, `http://${EMULATOR_HOST}:${EMULATOR_PORTS.auth}`, { disableWarnings: true });
  connectFirestoreEmulator(db, EMULATOR_HOST, EMULATOR_PORTS.firestore);
  connectFunctionsEmulator(functions, EMULATOR_HOST, EMULATOR_PORTS.functions);
  document.title = `[Emulators] ${document.title}`;
  const banner = document.getElementById("emulatorBanner");
  if (banner) banner.hidden = false;
  const localAdminBtn = document.getElementById("localAdminSignInBtn");
  if (localAdminBtn) localAdminBtn.hidden = false;
  console.info(`🧪 [Emulators] Auth, Firestore and Functions on ${EMULATOR_HOST}; App Check sends an unsigned token.`);
}

// Initialize Firebase App Check BEFORE AI instance initialization
let appCheck = null;
try {
  // On the emulators reCAPTCHA cannot run (the key is not valid for localhost).
  const appCheckProvider = useEmulators
    ? new CustomProvider({
        getToken: async () => ({ token: fakeAppCheckToken(firebaseConfig.appId), expireTimeMillis: Date.now() + 3600 * 1000 })
      })
    : new ReCaptchaV3Provider("6Lda8XMtAAAAADPl5OLRnzvpM5B2I_wCH8wuNbb1");
  appCheck = initializeAppCheck(app, {
    provider: appCheckProvider,
    isTokenAutoRefreshEnabled: true
  });
  console.log("🛡️ [AppCheck] Successfully initialized AppCheck BEFORE AI service.");
} catch (err) {
  console.warn("⚠️ [AppCheck] Initialization warning:", err.message);
}

// Initialize Firebase AI Instance AFTER App Check is initialized
const ai = getAI(app, { backend: new GoogleAIBackend() });

// Global State
let currentUser = null;
let isAdminUser = false;
let catalogRecipes = {};
let masterIngredients = {};
let categoriesData = {};
// globals/coverage_targets (recipe-builder's goals): undefined until the listener answers, null if missing or unreadable.
let coverageTargets = undefined;
let currentSection = "dashboard";

// Reads that wait for an admin (#1333), and listeners that stop with the sign-out (#1395). Every
// section's template is mounted as soon as the page loads, signed in or not, and a section that
// read right then was refused on the sign-in screen, where feedback's refusal showed as an error
// toast. A load given to loadForAdmin runs once its section is mounted and onAuthStateChanged has
// let an admin in, whichever comes last, and again at every later sign-in. A load that starts a
// listener returns the function that stops it: stopAdminListeners runs those when
// onAuthStateChanged reports nobody or a non-admin, and startAdminLoads before it starts the loads
// again, so that a second sign-in on the same page leaves each document one listener, not two.
const adminLoads = [];
let adminListenerStops = [];

function loadForAdmin(load) {
  adminLoads.push(load);
  if (isAdminUser) runAdminLoad(load);
}

function runAdminLoad(load) {
  const stop = load();
  if (typeof stop === "function") adminListenerStops.push(stop);
}

function startAdminLoads() {
  stopAdminListeners();
  adminLoads.forEach(runAdminLoad);
}

function stopAdminListeners() {
  const stops = adminListenerStops;
  adminListenerStops = [];
  stops.forEach((stop) => stop());
}

// Toasts: js/ui/toast.js

// Global Dynamic Floating Tooltip System
function initGlobalTooltipSystem() {
  let tooltipEl = document.getElementById("globalTooltip");
  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = "globalTooltip";
    tooltipEl.setAttribute("role", "tooltip");
    // Fixed over the body so no container clips it; data-visible fades it in.
    tooltipEl.className =
      "pointer-events-none fixed z-[999999] w-max max-w-[300px] translate-y-1 rounded-md bg-gray-900 px-3 py-2 text-left text-xs font-medium " +
      "whitespace-pre-wrap text-white opacity-0 shadow-lg transition duration-150 data-visible:translate-y-0 data-visible:opacity-100 " +
      "dark:bg-gray-700 dark:outline dark:-outline-offset-1 dark:outline-white/10";
    document.body.appendChild(tooltipEl);
  }

  document.addEventListener("mouseover", (e) => {
    const target = e.target.closest("[data-tooltip]");
    if (!target) {
      delete tooltipEl.dataset.visible;
      return;
    }

    const text = target.getAttribute("data-tooltip");
    if (!text) return;

    tooltipEl.textContent = text;
    tooltipEl.dataset.visible = "";

    const rect = target.getBoundingClientRect();
    const tooltipRect = tooltipEl.getBoundingClientRect();

    let top = rect.top - tooltipRect.height - 8;
    if (top < 8) {
      top = rect.bottom + 8;
    }

    let left = rect.left + rect.width / 2 - tooltipRect.width / 2;
    const maxLeft = window.innerWidth - tooltipRect.width - 12;
    left = Math.max(12, Math.min(left, maxLeft));

    tooltipEl.style.top = `${top}px`;
    tooltipEl.style.left = `${left}px`;
  });

  document.addEventListener("mouseout", (e) => {
    const target = e.target.closest("[data-tooltip]");
    if (target) {
      delete tooltipEl.dataset.visible;
    }
  });

  window.addEventListener("scroll", () => delete tooltipEl.dataset.visible, { passive: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initGlobalTooltipSystem);
} else {
  initGlobalTooltipSystem();
}

// Every button with data-copy, on any screen (#1427)
installCopyButtons(document, showToast);

// Slug Generator
function generateSlug(text) {
  if (!text) return "";
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/[\s-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * 1. Signs in with Google: a popup on desktop, a redirect on phones and tablets
 * (js/utils/googleSignIn.js). A redirect leaves the page and never resolves; the user comes back
 * signed in through onAuthStateChanged, and a failure through getRedirectResult below.
 */
export async function loginWithGoogle() {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  if (prefersRedirectSignIn(navigator)) return signInWithRedirect(auth, provider);
  const result = await signInWithPopup(auth, provider);
  return result.user;
}

/**
 * 2. Signs out the current admin user
 */
export async function logoutAdmin() {
  await signOut(auth);
}

// Keeps the public-page preview cookie in step with the signed-in user's ID token (cleared on sign-out)
onIdTokenChanged(auth, syncPreviewCookie);

let authStateReported = false;

/**
 * 3. Listens to persistent Firebase Auth state changes. Whether the user has the custom claim
 * { admin: true }, and nothing else, decides what they see (js/utils/adminAccess.js).
 */
onAuthStateChanged(auth, async (user) => {
  const firstReport = !authStateReported;
  authStateReported = true;
  const authScreen = document.getElementById("authScreen");
  const accessDeniedScreen = document.getElementById("accessDeniedScreen");
  const appScreen = document.getElementById("app");

  if (user) {
    const { isAdmin, error } = await checkAdminAccess(user);
    if (isAdmin) {
      currentUser = user;
      isAdminUser = true;
      showSignedInUser(user);
      authScreen.hidden = true;
      accessDeniedScreen.hidden = true;
      appScreen.hidden = false;
      switchSection(getSectionFromUrl(), false);
      showToast(`Welcome back, ${user.displayName || "Admin"}!`, "success");
      loadSubscriptionMetrics({ functions, httpsCallable });
      loadStoreRatings({ db, doc, getDoc, functions, httpsCallable, showToast });
      loadUsageLimits({ functions, httpsCallable, db, doc, setDoc, serverTimestamp, auth, showToast });
      startAdminLoads();
    } else {
      currentUser = null;
      isAdminUser = false;
      stopAdminListeners();
      await logoutAdmin();
      authScreen.hidden = true;
      accessDeniedScreen.hidden = false;
      appScreen.hidden = true;
      document.getElementById("accessDeniedDetail").textContent = accessDeniedMessage(user.email, error);
    }
  } else {
    currentUser = null;
    isAdminUser = false;
    stopAdminListeners();
    authScreen.hidden = false;
    accessDeniedScreen.hidden = true;
    appScreen.hidden = true;
    // ?emulators=admin signs in only when a page load's first auth state is "nobody", so Sign Out
    // still signs out, on a page that loaded with its session restored too (#1395: before, that
    // page's first Sign out ran the auto sign-in, which had not run at its load).
    if (firstReport && useEmulators && wantsLocalAdminSignIn(window.location)) signInAsLocalAdmin();
  }
});

// The user menu in the top bar: name, initials and email of whoever is signed in.
function showSignedInUser(user) {
  const name = user.displayName || user.email || "Admin";
  const initials = name.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((part) => part[0].toUpperCase()).join("") || "A";
  document.getElementById("currentUserName").textContent = name;
  document.getElementById("currentUserInitials").textContent = initials;
  document.getElementById("currentUserEmail").textContent = user.email || `${user.uid} (no email)`;
}

async function signInAsLocalAdmin() {
  try {
    await signInWithCustomToken(auth, localAdminCustomToken());
  } catch (err) {
    console.error("Local admin sign-in failed:", err);
    showToast(`Local admin sign-in failed (is tools/local-e2e/bin/up.sh running?): ${err.message}`, "error");
  }
}

document.getElementById("localAdminSignInBtn")?.addEventListener("click", () => signInAsLocalAdmin());

function showGoogleSignInError(err) {
  console.error("Google login failed:", err);
  const authErrorMsg = document.getElementById("authErrorMsg");
  if (authErrorMsg) {
    authErrorMsg.textContent = err.message || "Google Sign-In failed.";
    authErrorMsg.hidden = false;
  }
  showToast("Google Sign-In failed: " + err.message, "error");
}

// Interactive Google Sign-In Handler
document.getElementById("googleSignInBtn")?.addEventListener("click", async () => {
  const authErrorMsg = document.getElementById("authErrorMsg");
  if (authErrorMsg) authErrorMsg.hidden = true;
  try {
    const user = await loginWithGoogle();
    const { isAdmin, error } = await checkAdminAccess(user);
    if (isAdmin) {
      showToast("Welcome back! Verified Admin session.", "success");
    } else {
      await logoutAdmin();
      showToast(accessDeniedMessage(user.email, error), "error");
    }
  } catch (err) {
    if (!isSupersededSignIn(err)) showGoogleSignInError(err);
  }
});

// Back from a redirect sign-in that failed: show why, as a failed popup does.
getRedirectResult(auth).catch(showGoogleSignInError);

document.getElementById("logoutBtn")?.addEventListener("click", () => logoutAdmin());
document.getElementById("deniedLogoutBtn")?.addEventListener("click", () => logoutAdmin());

// 3. Navigation Controller & URL Router
const VALID_SECTIONS = ["dashboard", "recipes", "ingredients", "categories", "duplicates", "matcher", "recipe-view", "recipe-new", "users", "families", "import-cache", "inbox", "history", "feedback", "pricing", "store-search", "kroger-check", "leads", "announcements", "alerts", "billing"];

function getSectionFromUrl() {
  const hash = window.location.hash.replace("#", "").trim();
  const baseSection = hash.split("?")[0].split("/")[0].toLowerCase();
  return VALID_SECTIONS.includes(baseSection) ? baseSection : "dashboard";
}

// The sidebar (desktop and the mobile drawer) comes from js/ui/shell.js. A plain click switches
// in place, as before; a modified click opens the section's #link in a new tab.
renderNav();
document.querySelectorAll("[data-nav]").forEach((list) => {
  list.addEventListener("click", (e) => {
    const link = e.target.closest("[data-section]");
    if (!link || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    switchSection(link.dataset.section);
    closeMobileSidebar();
  });
});
// ⌘1–⌘9 go to the sections the sidebar numbers while ⌘ is held (#1596).
initNavShortcuts((section) => {
  switchSection(section);
  closeMobileSidebar();
});

// ⌘K: sections, recipes and ingredients from the data in memory, and the user search.
initCommandPalette({
  getRecipes: () => catalogRecipes,
  getIngredients: () => masterIngredients,
  goToSection: (section) => switchSection(section),
  openRecipe: (id) => viewRecipeFromModule(id),
  openIngredient: (key) => {
    switchSection("ingredients");
    openIngredientInspector(key);
  },
  findUser: (query) => {
    switchSection("users");
    const input = document.getElementById("userSearchInput");
    if (input) input.value = query;
    if (query) performUserSearch(query);
  }
});

function switchSection(sectionName, updateHash = true) {
  if (!VALID_SECTIONS.includes(sectionName)) sectionName = "dashboard";
  currentSection = sectionName;
  // A conversation is a page inside the Inbox (#1541): leaving the section closes it and saves its draft.
  if (sectionName !== "inbox") leaveInbox();

  if (updateHash) {
    if (sectionName !== "recipe-view" && sectionName !== "recipe-new" && window.location.hash !== `#${sectionName}`) {
      window.history.pushState(null, "", `#${sectionName}`);
    }
  }

  setActiveNav(sectionName);

  // "" rather than "block": the dashboard is a flex column (.dash), and an inline display drops its gaps.
  const secDash = document.getElementById("sectionDashboard");
  if (secDash) secDash.style.display = sectionName === "dashboard" ? "" : "none";

  const secRec = document.getElementById("sectionRecipes");
  if (secRec) secRec.style.display = sectionName === "recipes" ? "block" : "none";

  const secIng = document.getElementById("sectionIngredients");
  if (secIng) secIng.style.display = sectionName === "ingredients" ? "block" : "none";

  const secCat = document.getElementById("sectionCategories");
  if (secCat) secCat.style.display = sectionName === "categories" ? "block" : "none";

    const secMatcher = document.getElementById("sectionMatcher");
  if (secMatcher) secMatcher.style.display = sectionName === "matcher" ? "block" : "none";
  const secDup = document.getElementById("sectionDuplicates");
  if (secDup) secDup.style.display = sectionName === "duplicates" ? "block" : "none";

  const secView = document.getElementById("sectionRecipeView");
  if (secView) secView.style.display = sectionName === "recipe-view" ? "block" : "none";

  const secNew = document.getElementById("sectionRecipeNew");
  if (secNew) secNew.style.display = sectionName === "recipe-new" ? "block" : "none";

  const secUsers = document.getElementById("sectionUsers");
  if (secUsers) secUsers.style.display = sectionName === "users" ? "block" : "none";

  const secFamilies = document.getElementById("sectionFamilies");
  if (secFamilies) secFamilies.style.display = sectionName === "families" ? "block" : "none";

  const secImportCache = document.getElementById("sectionImportCache");
  if (secImportCache) secImportCache.style.display = sectionName === "import-cache" ? "block" : "none";

  const secInbox = document.getElementById("sectionInbox");
  if (secInbox) secInbox.style.display = sectionName === "inbox" ? "block" : "none";

  const secHistory = document.getElementById("sectionHistory");
  if (secHistory) secHistory.style.display = sectionName === "history" ? "block" : "none";

  const secFeedback = document.getElementById("sectionFeedback");
  if (secFeedback) secFeedback.style.display = sectionName === "feedback" ? "block" : "none";

  const secPricing = document.getElementById("sectionPricing");
  if (secPricing) secPricing.style.display = sectionName === "pricing" ? "block" : "none";

  const secStoreSearch = document.getElementById("sectionStoreSearch");
  if (secStoreSearch) secStoreSearch.style.display = sectionName === "store-search" ? "block" : "none";

  const secKrogerCheck = document.getElementById("sectionKrogerCheck");
  if (secKrogerCheck) secKrogerCheck.style.display = sectionName === "kroger-check" ? "block" : "none";

  const secLeads = document.getElementById("sectionLeads");
  if (secLeads) secLeads.style.display = sectionName === "leads" ? "block" : "none";

  const secAnnouncements = document.getElementById("sectionAnnouncements");
  if (secAnnouncements) secAnnouncements.style.display = sectionName === "announcements" ? "block" : "none";

  const secAlerts = document.getElementById("sectionAlerts");
  if (secAlerts) secAlerts.style.display = sectionName === "alerts" ? "block" : "none";

  const secBilling = document.getElementById("sectionBilling");
  if (secBilling) secBilling.style.display = sectionName === "billing" ? "block" : "none";

  loadSectionData(sectionName);

  const titles = {
    dashboard: "Dashboard Overview",
    recipes: "Recipes Catalog Manager",
    ingredients: "Master Ingredients Registry",
    categories: "Categories & Dietary Styles",
    duplicates: "Duplicate Ingredients Resolver",
    matcher: "Semantic Ingredient Matcher Diagnostic Console",
    "recipe-view": "Recipe Document Viewer",
    "recipe-new": "Create & Import New Recipe",
    users: "User Diagnostics & Developer Mode",
    families: "Families",
    "import-cache": "Smart Import Cache",
    inbox: "Customer Inbox",
    history: "Contact History",
    feedback: "User App Feedback & Ratings",
    pricing: "App Store Pricing & Storefront Matrix",
    "store-search": "App Store Search Suggestions",
    "kroger-check": "Kroger Check",
    leads: "Waitlist & Recipe Rescue Leads",
    announcements: "System Announcements & App Pause",
    alerts: "Alerts",
    billing: "Billing"
  };
  const pageTitle = document.getElementById("pageTitle");
  if (pageTitle) {
    if (sectionName === "recipe-view" || sectionName === "recipe-new") {
      pageTitle.hidden = true;
    } else {
      pageTitle.hidden = false;
      pageTitle.textContent = titles[sectionName] || "Dashboard";
    }
  }
}

// What a section reads as it opens, and only for an admin (#1333): hashchange switches sections
// on the sign-in screen too (Back after signing out), where the reads are refused.
// onAuthStateChanged switches to the section again once an admin is in.
function loadSectionData(sectionName) {
  if (!isAdminUser) return;

  if (sectionName === "recipe-view") {
    renderRecipeViewSectionFromModule(db, getDoc, doc, showToast, masterIngredients, categoriesData, { collection, getDocs });
  } else if (sectionName === "recipe-new") {
    renderRecipeNewSectionFromModule();
  } else if (sectionName === "inbox") {
    enterInbox();
  } else if (sectionName === "history") {
    openHistoryFromHash();
  } else if (sectionName === "feedback") {
    fetchFeedbackList();
    fetchRecipeRatings();
  } else if (sectionName === "users") {
    openUserFromHash();
  } else if (sectionName === "families") {
    openFamilyFromHash();
  } else if (sectionName === "pricing") {
    fetchAndRenderPricing(db, doc, getDoc);
  } else if (sectionName === "leads") {
    fetchLeadsList();
    fetchPromoInventoryStock();
  } else if (sectionName === "announcements") {
    fetchAnnouncementHistory();
  } else if (sectionName === "alerts") {
    openAlerts();
  } else if (sectionName === "billing") {
    openBilling();
  }
}

// #users?uid=<uid> (a user id on a feedback, #1421) runs the User Diagnostics search for that user.
// The sidebar's own link pushes a bare #users first, so it opens the screen empty as before.
function openUserFromHash() {
  const uid = userIdFromHash(window.location.hash);
  if (!uid) return;
  const input = document.getElementById("userSearchInput");
  const clearBtn = document.getElementById("userSearchClearBtn");
  if (input) input.value = uid;
  if (clearBtn) clearBtn.style.display = "inline-flex";
  performUserSearch(uid);
}

// A change of the hash fires popstate as well as hashchange, so the router listens to hashchange
// only. With a listener on each, every address change through the hash (the recipe view sets
// location.hash; Back and Forward) switched section twice and read that section's data twice (#1395).
window.addEventListener("hashchange", () => switchSection(getSectionFromUrl(), false));

// 4. Realtime Firestore Listeners & Data Binding: the catalog's documents, through
// js/catalogListeners.js. Like every other admin load, the listeners start at each sign-in and
// stop at the sign-out (#1395).
function initCRMRealtimeData() {
  return listenToCatalog({ db, doc, onSnapshot }, {
    // globals/admin_catalog_summary, or globals/catalog_summary while that has no recipes
    onRecipes(recipes) {
      catalogRecipes = recipes;
      updateActiveCatalogRecipesFromModule(catalogRecipes);
      renderDashboardStats();
      renderRecipesTable();
    },

    // globals/master_ingredients, with robust schema normalization
    onIngredients(raw) {
      let itemsMap = {};

      if (raw.ingredients && typeof raw.ingredients === "object" && !Array.isArray(raw.ingredients)) {
        itemsMap = raw.ingredients;
      } else if (Array.isArray(raw.ingredients)) {
        raw.ingredients.forEach((item) => {
          const k = item.uid || item.id || item.name;
          if (k) itemsMap[k] = item;
        });
      } else if (Array.isArray(raw.items)) {
        raw.items.forEach((item) => {
          const k = item.uid || item.id || item.name;
          if (k) itemsMap[k] = item;
        });
      } else {
        Object.keys(raw).forEach((k) => {
          if (k !== "count" && k !== "updated_at" && typeof raw[k] === "object") {
            itemsMap[k] = raw[k];
          }
        });
      }

      masterIngredients = itemsMap;
      if (raw.image_url_prefix) {
        updateActiveMasterIngredientsPrefixFromModule(raw.image_url_prefix);
      }
      updateActiveMasterIngredientsFromModule(masterIngredients);
      updateActiveMasterIngredientsInRecipesModule(masterIngredients);
      renderDashboardStats();
      renderIngredientsTable();
      findDuplicateClusters();
    },

    // globals/categories
    onCategories(data) {
      categoriesData = data;
      updateActiveCategoriesDataFromModule(categoriesData);
      updateActiveCategoriesDataInRecipesModule(categoriesData);
      renderDashboardStats();
      renderCategoriesView();
    },

    // globals/coverage_targets, the catalog goals recipe-builder's sync publishes (admin-only)
    onCoverageTargets(targets) {
      coverageTargets = targets;
      renderDashboardStats();
    }
  });
}
loadForAdmin(initCRMRealtimeData);

// Mount Dashboard Pure HTML Partial Template
async function initDashboardComponent() {
  await loadDashboardTemplate();
  rememberDisclosures(document.getElementById("sectionDashboard") || document);
  renderSubscriptionsPanel();
  renderStoreRatingsPanel();
  renderUsagePanel();
  switchSection(currentSection, false);
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initDashboardComponent);
} else {
  initDashboardComponent();
}

function renderDashboardStats() {
  renderDashboardStatsFromModule(catalogRecipes, categoriesData, coverageTargets);
}

// Mount Recipe Catalog Pure HTML Partial Template
async function initRecipesComponent() {
  await loadRecipesTemplate();
  switchSection(currentSection, false);
  if (catalogRecipes && Object.keys(catalogRecipes).length > 0) {
    renderRecipesTable();
  }
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initRecipesComponent);
} else {
  initRecipesComponent();
}

// Delegated Recipe Catalog & Editor Functions
function renderRecipesTable() {
  renderRecipeTableFromModule(catalogRecipes);
}

async function openRecipeEditor(id = null) {
  // The table works from the catalog summary (no created_by), so the guard reads the recipe document itself.
  const editable = await checkRecipeEditable(id, { db, doc, getDoc });
  if (!editable.allowed) {
    showToast(editable.message, "danger");
    return;
  }
  openRecipeEditorFromModule(id, catalogRecipes, masterIngredients);
}

/** Blocks opening the recipe view's content editors on recipes managed in recipe-builder. */
async function recipeViewEditAllowed() {
  const recipe = getCurrentRecipeViewDataFromModule();
  const editable = await checkRecipeEditable(recipe?.id || "current", { knownRecipe: recipe });
  if (!editable.allowed) showToast(editable.message, "danger");
  return editable.allowed;
}

function addIngredientRow(data = {}) {
  addIngredientRowFromModule(masterIngredients, data);
}

function addInstructionRow(text = "") {
  addInstructionRowFromModule(text);
}

async function saveRecipe() {
  await saveRecipeFromModule(db, catalogRecipes, masterIngredients, setDoc, doc, showToast, dismissToast, renderRecipesTable, getDoc, auth);
}

async function deleteRecipe(id) {
  await deleteRecipeFromModule(id, db, catalogRecipes, deleteDoc, doc, showToast, dismissToast, renderRecipesTable);
}

async function bulkDeleteRecipes() {
  await bulkDeleteRecipesFromModule(db, catalogRecipes, writeBatch, doc, showToast, dismissToast, renderRecipesTable);
}

async function bulkUpdateRecipeStatus(status) {
  await bulkUpdateRecipeStatusFromModule(status, db, catalogRecipes, writeBatch, doc, showToast, dismissToast, renderRecipesTable);
}

async function bulkUpdateRecipeSignature(isSignature) {
  await bulkUpdateRecipeSignatureFromModule(isSignature, db, catalogRecipes, writeBatch, doc, showToast, dismissToast, renderRecipesTable);
}

function printRecipeJson(id = null) {
  printRecipeJsonFromModule(id, catalogRecipes, showToast);
}

// Category Display Name Helpers
function getAllergenDisplayName(allergenId) {
  if (!allergenId) return "";
  const list = categoriesData?.allergens || [];
  let found = null;
  if (Array.isArray(list)) {
    found = list.find((item) => (item.id || item.code || item.key) === allergenId);
  } else if (typeof list === "object") {
    found = list[allergenId];
  }
  if (found && (found.name || found.display_name)) {
    const nameStr = found.name || found.display_name;
    return (found.emoji ? found.emoji + " " : "") + nameStr;
  }
  return allergenId.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

function getDietaryDisplayName(dietId) {
  if (!dietId) return "";
  const list = categoriesData?.dietary_preferences || [];
  let found = null;
  if (Array.isArray(list)) {
    found = list.find((item) => (item.id || item.code || item.key) === dietId);
  } else if (typeof list === "object") {
    found = list[dietId];
  }
  if (found && (found.name || found.display_name)) {
    const nameStr = found.name || found.display_name;
    return (found.emoji ? found.emoji + " " : "") + nameStr;
  }
  return dietId.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

// Extensible Master Ingredient Validation Engine (Delegated to ingredientsController.js)

// Mount Master Ingredients Pure HTML Partial Template
async function initIngredientsComponent() {
  await loadIngredientsTemplate();
  switchSection(currentSection, false);
  if (masterIngredients && Object.keys(masterIngredients).length > 0) {
    renderIngredientsTable();
  }
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initIngredientsComponent);
} else {
  initIngredientsComponent();
}

// Delegated Master Ingredients Functions
function setIngredientSort(col) {
  setIngredientSortFromModule(col, renderIngredientsTable);
}

function renderIngredientsTable() {
  renderIngredientsTableFromModule(masterIngredients, categoriesData);
}

function openIngredientInspector(key) {
  openIngredientInspectorFromModule(key, masterIngredients, categoriesData);
}

function openIngredientEditor(key = null) {
  openIngredientEditorFromModule(key, masterIngredients, categoriesData);
}

async function saveMasterIngredient() {
  await saveMasterIngredientFromModule(db, masterIngredients, setDoc, doc, showToast, dismissToast, renderIngredientsTable);
}

async function deleteMasterIngredient(key) {
  await deleteMasterIngredientFromModule(key, db, masterIngredients, setDoc, doc, showToast, dismissToast, renderIngredientsTable);
}

function copyRawIngredient(key) {
  copyRawIngredientFromModule(masterIngredients, showToast);
}

async function applyIngredientJsonImport() {
  await applyIngredientJsonImportFromModule(db, setDoc, doc, showToast, dismissToast, renderIngredientsTable);
}

// Mount Categories Pure HTML Partial Template
async function initCategoriesComponent() {
  await loadCategoriesTemplate();
  switchSection(currentSection, false);
  if (categoriesData && Object.keys(categoriesData).length > 0) {
    renderCategoriesView();
  }
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initCategoriesComponent);
} else {
  initCategoriesComponent();
}

// 8. Categories View & Manager (Delegated to categoriesController.js)
function toggleCategoriesRawJson() {
  toggleCategoriesRawJsonFromModule(categoriesData);
}

function renderCategoriesView() {
  renderCategoriesViewFromModule(categoriesData);
}

async function saveCategoriesDocument(overrideData = null) {
  const dataToSave = overrideData || getActiveCategoriesDataFromModule() || categoriesData;
  await saveCategoriesDocumentFromModule(db, dataToSave, setDoc, doc, showToast, dismissToast);
}

function toggleCategoryFeatured(sectionKey, itemId, subId = null) {
  toggleCategoryFeaturedFromModule(sectionKey, itemId, subId);
}

function deleteCategoryItem(sectionKey, itemId, subId = null) {
  deleteCategoryItemFromModule(sectionKey, itemId, subId);
}

function openCategoryModal(sectionKey, itemId = null, subId = null, isEdit = false) {
  openCategoryModalFromModule(sectionKey, itemId, subId, isEdit);
}

function saveCategoryModalSubmit() {
  saveCategoryModalSubmitFromModule();
}

// Every modal is a <dialog> in an <el-dialog> (#1212): it closes through js/ui/dialog.js and
// handles Esc and a click outside itself.
function closeModal(modalId) {
  hideDialog(modalId);
}

// Mount Duplicates Pure HTML Partial Template
async function initDuplicatesComponent() {
  await loadDuplicatesTemplate();
  switchSection(currentSection, false);
  if (masterIngredients && Object.keys(masterIngredients).length > 0) {
    findDuplicateClusters();
  }
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initDuplicatesComponent);
} else {
  initDuplicatesComponent();
}

// Mount User Diagnostics & Developer Mode Partial Template
async function initUsersComponent() {
  await loadUsersTemplate();
  initUsersController({
    db, getDoc, doc, setDoc, collection, query, where, getDocs, functions, httpsCallable, showToast,
    // Test users (#1922): the switch's reason, and who saved the list when
    arrayUnion, arrayRemove, serverTimestamp, auth
  });
  switchSection(currentSection, false);
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initUsersComponent);
} else {
  initUsersComponent();
}

// Mount the Families screen (#1809). It reads nothing until an admin searches or opens #families?id=.
async function initFamiliesComponent() {
  await loadFamiliesTemplate();
  initFamiliesController({ functions, httpsCallable, showToast });
  switchSection(currentSection, false);
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initFamiliesComponent);
} else {
  initFamiliesComponent();
}

// Mount the Smart import cache screen (#1562). It reads nothing until an admin looks something up.
async function initImportCacheComponent() {
  await loadImportCacheTemplate();
  initImportCacheController({ functions, httpsCallable, showToast });
  switchSection(currentSection, false);
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initImportCacheComponent);
} else {
  initImportCacheComponent();
}

// Mount the Customer Inbox (#1518). Its reads start when the section opens (loadSectionData); the 30-second check
// runs while an admin is signed in and the section is on screen, and stops at the sign-out.
async function initInboxComponent() {
  await loadInboxTemplate();
  initInboxController({
    db, collection, query, where, orderBy, limit, getDocs, getDoc, doc, getCountFromServer, functions, httpsCallable,
    showToast, dismissToast, currentUid: () => currentUser?.uid || null,
    currentUserName: () => currentUser?.displayName || currentUser?.email || ""
  });
  loadForAdmin(startInboxAutoSync);
  switchSection(currentSection, false);
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initInboxComponent);
} else {
  initInboxComponent();
}

// Mount the contact history (#1925). It reads nothing until an admin asks for someone (loadSectionData opens
// #history?uid= / ?email= once one is signed in).
async function initHistoryComponent() {
  await loadHistoryTemplate();
  initHistoryController({ functions, httpsCallable, showToast });
  switchSection(currentSection, false);
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initHistoryComponent);
} else {
  initHistoryComponent();
}

// Mount User Feedback Partial Template
async function initFeedbackComponent() {
  await loadFeedbackTemplate();
  initFeedbackController({
    db, getDocs, collection, collectionGroup, query, orderBy, limit, doc, writeBatch, serverTimestamp, showToast,
    handledBy: () => currentUser?.email || currentUser?.uid || null,
    // The recipe ratings' titles come from the catalog summary the recipes section listens to.
    recipeTitle: (id) => catalogRecipes?.[id]?.title || null
  });
  loadForAdmin(fetchFeedbackList);
  loadForAdmin(fetchRecipeRatings);
  switchSection(currentSection, false);
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initFeedbackComponent);
} else {
  initFeedbackComponent();
}

// Mount App Store Pricing Partial Template
async function initPricingComponent() {
  await loadPricingTemplate();
  initPricingController({ db, functions, httpsCallable, doc, getDoc, showToast });
  loadForAdmin(() => fetchAndRenderPricing(db, doc, getDoc));
  switchSection(currentSection, false);
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initPricingComponent);
} else {
  initPricingComponent();
}

// Mount Store Search Partial Template (#1800): it reads nothing until the admin searches.
async function initStoreSearchComponent() {
  await loadStoreSearchTemplate();
  initStoreSearchController({ functions, httpsCallable, showToast });
  switchSection(currentSection, false);
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initStoreSearchComponent);
} else {
  initStoreSearchComponent();
}

// Mount Kroger Check Partial Template (#15): it calls Kroger only when the admin runs the check.
async function initKrogerCheckComponent() {
  await loadKrogerCheckTemplate();
  initKrogerCheckController({ functions, httpsCallable, showToast });
  switchSection(currentSection, false);
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initKrogerCheckComponent);
} else {
  initKrogerCheckComponent();
}

// Mount Alerts (#2314): Cloud Monitoring's alerts, read once an admin is in (the nav badge counts the open ones), on
// opening the section, and every 2 minutes while it is on screen.
async function initAlertsComponent() {
  await loadAlertsTemplate();
  initAlertsController({ functions, httpsCallable, showToast });
  loadForAdmin(() => {
    refreshAlerts();
    return startAlertsAutoRefresh();
  });
  switchSection(currentSection, false);
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initAlertsComponent);
} else {
  initAlertsComponent();
}

// Mount Billing (#2539): the month's costs and what is still missing, read when an admin opens the section.
async function initBillingComponent() {
  await loadBillingTemplate();
  initBillingController({ functions, httpsCallable, showToast, db, collection, getDocs, doc, setDoc, deleteDoc, serverTimestamp, auth });
  switchSection(currentSection, false);
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initBillingComponent);
} else {
  initBillingComponent();
}

// Mount Waitlist Leads Partial Template
async function initLeadsComponent() {
  await loadLeadsTemplate();
  initLeadsController({ db, getDocs, collection, query, orderBy, limit, getCountFromServer, doc, setDoc, writeBatch, where, functions, httpsCallable, showToast });
  switchSection(currentSection, false);
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initLeadsComponent);
} else {
  initLeadsComponent();
}


// Mount Semantic Ingredient Matcher Diagnostic Partial Template
async function initMatcherComponent() {
  await loadMatcherTemplate();
  initMatcherController({ functions, httpsCallable, showToast });
  switchSection(currentSection, false);
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initMatcherComponent);
} else {
  initMatcherComponent();
}

// Mount System Announcements & App Pause Partial Template
async function initAnnouncementsComponent() {
  await loadAnnouncementsTemplate();
  initAnnouncementsController({ db, doc, getDoc, setDoc, onSnapshot, collection, getDocs, query, orderBy, limit, showToast, dismissToast });
  loadForAdmin(loadAnnouncements);
  switchSection(currentSection, false);
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initAnnouncementsComponent);
} else {
  initAnnouncementsComponent();
}

// Delegated Duplicate Resolver Functions
function normalizeIngredientKey(key, name) {
  return normalizeIngredientKeyFromModule(key, name);
}

function findDuplicateClusters() {
  findDuplicateClustersFromModule(masterIngredients, normalizeIngredientKeyFromModule);
}

function refreshDuplicatesEngine() {
  refreshDuplicatesEngineFromModule(masterIngredients, normalizeIngredientKeyFromModule, showToast);
}

function renderDuplicatesSection() {
  renderDuplicatesSectionFromModule(masterIngredients);
}

async function autoMergeAllCleanClusters() {
  await autoMergeAllCleanClustersFromModule(db, masterIngredients, setDoc, doc, showToast, dismissToast, renderIngredientsTable, renderDashboardStats, normalizeIngredientKeyFromModule);
}

function openMergeDiffModal(clusterIndex) {
  openMergeDiffModalFromModule(clusterIndex, masterIngredients);
}

function setSurvivingUid(uid) {
  setSurvivingUidFromModule(uid);
}

function selectMergeField(fieldKey, candidateKey) {
  selectMergeFieldFromModule(fieldKey, candidateKey);
}

async function executeIngredientMerge() {
  await executeIngredientMergeFromModule(db, masterIngredients, setDoc, doc, showToast, dismissToast, renderIngredientsTable, renderDashboardStats, normalizeIngredientKey);
}

// Gemini runs from the browser against the production project, so it stays off on the emulators.
function geminiAllowed() {
  if (!useEmulators) return true;
  showToast("Gemini is off on the local emulators: it would call the production project.", "error");
  return false;
}

// Export Controller API to Window for Inline Handlers
window.crmApp = {
  switchTab: (section) => switchSection(section),
  switchSection: (section) => switchSection(section),
  openRecipeEditor,
  editRecipe: (id) => openRecipeEditor(id),
  viewRecipe: (id) => viewRecipeFromModule(id),
  toggleRecipeViewRawJson: () => toggleRecipeViewRawJsonFromModule(),
  setRecipeViewTimeline: (timeline) => setRecipeViewTimelineFromModule(timeline),
  setRecipeViewVariant: (variant) => setRecipeViewVariantFromModule(variant),
  quickFixIngredientUnit: (recipeId, uid, newUnit) => quickFixIngredientUnitFromModule(recipeId, uid, newUnit, db, setDoc, doc, showToast, dismissToast, getDoc),
  toggleInlineTaxonomyEdit: async (isEdit) => {
    if (isEdit && !(await recipeViewEditAllowed())) return;
    return toggleInlineTaxonomyEditFromModule(isEdit, categoriesData, db, doc, getDoc);
  },
  saveTaxonomy: () => saveTaxonomyFromModule(db, setDoc, doc, showToast, dismissToast, getDoc),
  updateTaxPillStyle: (labelEl) => updateTaxPillStyleFromModule(labelEl),
  toggleInlineCookwareEdit: (isEdit) => toggleInlineCookwareEditFromModule(isEdit),
  saveCookware: () => saveCookwareFromModule(db, setDoc, doc, showToast, dismissToast, getDoc, auth),
  toggleInlineMetricsEdit: (isEdit) => toggleInlineMetricsEditFromModule(isEdit),
  saveMetrics: () => saveMetricsFromModule(db, setDoc, doc, showToast, dismissToast, getDoc),
  toggleUnifiedRecipeEdit: async (isEdit) => {
    if (isEdit && !(await recipeViewEditAllowed())) return;
    return toggleUnifiedRecipeEditFromModule(isEdit);
  },
  addIngredientEditRow: (ingData) => addIngredientEditRowFromModule(ingData),
  addInstructionStepEditRow: (quadId, txt) => addInstructionStepEditRowFromModule(quadId, txt),
  reindexInstructionStepNumbers: (quadId) => reindexInstructionStepNumbersFromModule(quadId),
  saveUnifiedRecipe: () => saveUnifiedRecipeFromModule(db, setDoc, doc, showToast, dismissToast, getDoc, auth),
  openImportRecipeModal: () => openImportRecipeModalFromModule(),
  openAddRecipeJsonModal: () => openAddRecipeJsonModalFromModule(),
  pasteExampleRecipeJson: () => pasteExampleRecipeJsonFromModule(),
  handleRecipeJsonDragOver: (event) => handleRecipeJsonDragOverFromModule(event),
  handleRecipeJsonDragLeave: (event) => handleRecipeJsonDragLeaveFromModule(event),
  handleRecipeJsonDrop: (event) => handleRecipeJsonDropFromModule(event),
  submitAddRecipeJson: () => submitAddRecipeJsonFromModule(db, setDoc, doc, collection, showToast, dismissToast, auth, getDoc),
  switchImportTab: (mode) => switchImportTabFromModule(mode),
  submitImportRecipe: () => submitImportRecipeFromModule(functions, httpsCallable, showToast, dismissToast),
  requestNotificationPermission: async () => {
    const perm = await requestNotificationPermission();
    const promptEl = document.getElementById("importNotifPermPrompt");
    if (promptEl && perm === "granted") {
      promptEl.style.display = "none";
    }
    if (perm === "granted") {
      showToast("🔔 Desktop notifications enabled! You'll get alerted when recipes finish.", "success");
    } else if (perm === "denied") {
      showToast("⚠️ Notifications were blocked. You can unblock them in browser site settings.", "warning");
    }
    return perm;
  },
  renderRecipeNewSection: () => renderRecipeNewSectionFromModule(),
  saveNewRecipe: () => saveNewRecipeFromModule(db, setDoc, doc, showToast, dismissToast, collection, getDoc, auth),
  addNewRecipeIngredientRow: (ingData) => addNewRecipeIngredientRowFromModule(ingData),
  toggleNewRecipeRawJson: () => toggleNewRecipeRawJsonFromModule(),
  copyNewRecipeRawJson: () => copyNewRecipeRawJsonFromModule(showToast),
  handleColumnSort: (columnKey) => handleColumnSortFromModule(columnKey),
  onRecipeSortDropdownChange: () => onRecipeSortDropdownChangeFromModule(),
  addNewRecipeStepRow: (quadId) => addNewRecipeStepRowFromModule(quadId),
  removeNewRecipeStepRow: (button) => removeNewRecipeStepRowFromModule(button),
  setRecipeStatusFilter: (filter) => setRecipeStatusFilterFromModule(filter),
  toggleRecipePublishStatus: () => toggleRecipePublishStatusFromModule(db, setDoc, doc, showToast, dismissToast, getDoc, auth),
  onNewRecipeStatusChange: (status) => onNewRecipeStatusChangeFromModule(status),
  addRecipeNewCookwareChip: () => addRecipeNewCookwareChipFromModule(),
  removeRecipeNewCookwareChip: (idx) => removeRecipeNewCookwareChipFromModule(idx),
  generateAiPhotoForRecipe: () => geminiAllowed() && generateAiPhotoForRecipeFromModule(ai, getGenerativeModel, ResponseModality, functions, httpsCallable, showToast, dismissToast),
  regenerateRecipePhoto: () => geminiAllowed() && regenerateRecipePhotoFromModule(db, setDoc, doc, ai, getGenerativeModel, ResponseModality, functions, httpsCallable, showToast, dismissToast, getDoc, auth),
  triggerRecipePhotoUpload: (context) => triggerRecipePhotoUploadFromModule(context),
  handleRecipePhotoLocalUpload: (event, context) => uploadRecipePhotoFromLocalFromModule(event, context, { functions, httpsCallable, showToast, dismissToast, db, doc, setDoc, getDoc, auth }),
  renderRecipeAuditLogDOM: (metaData) => renderRecipeAuditLogDOMFromModule(metaData),
  refreshRecipeAuditLog: (recipeId) => refreshRecipeAuditLogFromModule(db, doc, getDoc, recipeId),
  copyRecipeSourceUrl: () => copyRecipeSourceUrlFromModule(showToast),
  saveRecipe,
  deleteRecipe,
  toggleRecipeSelection: (id, isSelected) => toggleRecipeSelectionFromModule(id, isSelected),
  toggleSelectAllRecipes: (selectAll) => toggleSelectAllRecipesFromModule(selectAll),
  clearRecipeSelection: () => clearRecipeSelectionFromModule(),
  getSelectedRecipeIds: () => getSelectedRecipeIdsFromModule(),
  bulkDeleteRecipes,
  bulkUpdateRecipeStatus: (status) => bulkUpdateRecipeStatus(status),
  bulkUpdateRecipeSignature: (isSignature) => bulkUpdateRecipeSignature(isSignature),
  printRecipeJson: (id = null) => printRecipeJson(id),
  addIngredientRow,
  addInstructionRow,
  openIngredientEditor,
  editMasterIngredient: (key) => openIngredientEditor(key),
  saveMasterIngredient,
  deleteMasterIngredient,
  copyRawIngredient,
  openIngredientInspector,
  getMasterIngredient: (uid) => masterIngredients[uid],
  getMasterIngredientsMap: () => masterIngredients,
  downloadMasterIngredientsJson: () => downloadMasterIngredientsJsonFromModule(masterIngredients),
  openIngredientImportJsonModal: () => openIngredientImportJsonModalFromModule(),
  rebuildPlanningIndex: () => rebuildPlanningIndexFromModule({ functions, httpsCallable, showToast }),
  onIngredientJsonInput: () => onIngredientJsonInputFromModule(),
  onIngredientJsonFileSelected: (event) => onIngredientJsonFileSelectedFromModule(event),
  applyIngredientJsonImport,
  getCategoriesData: () => categoriesData,
  toggleCategoryFeatured: (sectionKey, itemId, subId) => toggleCategoryFeatured(sectionKey, itemId, subId),
  deleteCategoryItem: (sectionKey, itemId, subId) => deleteCategoryItem(sectionKey, itemId, subId),
  openCategoryModal: (sectionKey, itemId, subId, isEdit) => openCategoryModal(sectionKey, itemId, subId, isEdit),
  saveCategoryModalSubmit: () => saveCategoryModalSubmit(),
  setIngredientSort,
  onIngredientSearchInput: () => onIngredientSearchInputFromModule(),
  onRecipeSearchInput: () => onRecipeSearchInputFromModule(),
  toggleIngredientAllergenTag: (tagId) => toggleIngredientAllergenTagFromModule(tagId),
  toggleIngredientDietTag: (tagId) => toggleIngredientDietTagFromModule(tagId),
  onSubstituteSearchInput: (query) => onSubstituteSearchInputFromModule(query),
  onSubstituteChosen: (value) => onSubstituteChosenFromModule(value),
  addSubstitute: (uid) => addSubstituteFromModule(uid),
  removeSubstitute: (uid) => removeSubstituteFromModule(uid),
  updateSubstituteReason: (uid, reason) => updateSubstituteReasonFromModule(uid, reason),
  openAddMissingIngredientForm: (uid) => openAddMissingIngredientFormFromModule(uid),
  validateIngredientDuplicates: () => validateIngredientDuplicatesFromModule(),
  autofillIngredientWithAI: () => geminiAllowed() && autofillIngredientWithAIFromModule(ai, getGenerativeModel, showToast, dismissToast, appCheck, getToken, SchemaType, categoriesData, masterIngredients),
  generateAiPhotoForIngredient: () => geminiAllowed() && generateAiPhotoForIngredientFromModule(ai, getGenerativeModel, ResponseModality, functions, httpsCallable, showToast, dismissToast, appCheck, getToken),
  onIngredientImagePathChanged: (val) => onIngredientImagePathChangedFromModule(val),
  refreshDuplicatesEngine,
  autoMergeAllCleanClusters,
  openMergeDiffModal,
  setSurvivingUid,
  selectMergeField,
  executeIngredientMerge,
  toggleCategoriesRawJson,
  saveCategoriesDocument,
  resetUserSearch: () => resetUserSearchState(),
  searchUser: (term) => performUserSearch(term),
  toggleUserDeveloperMode: (uid, status) => toggleUserDeveloperMode(uid, status),
  toggleUserTesterMode: (uid, status) => toggleUserTesterMode(uid, status),
  toggleUserAdminRole: (uid, status) => toggleUserAdminRole(uid, status),
  toggleSubRawJson: () => toggleSubRawJson(),
  copySubRawJson: () => copySubRawJson(showToast),
  fetchLeadsList: () => fetchLeadsList(),
  openLeadModal: (idx) => openLeadModal(idx),
  resetLeadClaim: (lead) => resetLeadClaim(lead),
  copyFilteredEmails: () => copyFilteredEmails(),
  exportLeadsCSV: () => exportLeadsCSV(),
  closeModal
};
